import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { X, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";

export default function UploadDesignModal({ open, onClose, productContext }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", room: "", budget: "", wood_preference: "", fabric_preference: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (open && productContext?.name) {
      setForm((f) => ({
        ...f,
        notes: f.notes || `Customising: ${productContext.name} — ${productContext.url || ""}`,
      }));
    }
  }, [open, productContext]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (file) fd.append("file", file);
      await axios.post(`${API}/design-enquiries`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(productContext?.name ? "Received. Our team will revert on this piece." : "Design received. Our team will match materials & revert.");
      onClose();
      setForm({ name: "", phone: "", email: "", room: "", budget: "", wood_preference: "", fabric_preference: "", notes: "" });
      setFile(null);
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  };

  const title = productContext?.name ? "Customise this piece." : "Send us a sketch. Get a piece.";
  const overline = productContext?.name ? `Customise · ${productContext.name}` : "Upload Your Design";
  const sub = productContext?.name
    ? "Change the fabric, wood, dimensions or shape. Attach any reference and our atelier will revert with a quote."
    : "Attach a Pinterest image, hand sketch or brief. Our studio will match materials, finishes and quote — direct from factory.";

  return (
    <div className="fixed inset-0 z-[70] bg-[#0B0B0C] overflow-y-auto fade-in" data-testid="upload-design-modal">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="flex justify-between items-start mb-10">
          <div>
            <div className="font-micro text-[#B8895C] mb-3">{overline}</div>
            <h1 className="font-serif text-[#EDE4D3] text-4xl md:text-5xl leading-tight">{title}</h1>
            <p className="text-[#8A8680] mt-4 max-w-xl">{sub}</p>
          </div>
          <button onClick={onClose} className="text-[#EDE4D3] hover:text-[#B8895C]" data-testid="upload-design-close"><X size={22} strokeWidth={1} /></button>
        </div>

        <form onSubmit={submit} className="grid md:grid-cols-2 gap-12">
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`aspect-square flex flex-col items-center justify-center cursor-pointer transition-colors duration-500 ${drag ? "border-[#B8895C]" : ""}`}
            style={{ border: `1px dashed ${drag ? "#B8895C" : "#2A2825"}`, background: "#141414" }}
            data-testid="upload-dropzone"
          >
            <UploadIcon size={32} strokeWidth={1} className="text-[#B8895C] mb-4" />
            {file ? (
              <>
                <div className="font-serif text-[#EDE4D3] text-xl">{file.name}</div>
                <div className="font-micro text-[#8A8680] mt-2">Click or drop to replace</div>
              </>
            ) : (
              <>
                <div className="font-serif text-[#EDE4D3] text-xl">Drag &amp; drop your image</div>
                <div className="font-micro text-[#8A8680] mt-2">or click to browse (optional)</div>
              </>
            )}
            <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <input className="input-teak" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="ud-name" />
              <input className="input-teak" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="ud-phone" />
            </div>
            <input className="input-teak" placeholder="Email (optional)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ud-email" />
            <div className="grid grid-cols-2 gap-6">
              <input className="input-teak" placeholder="Room (e.g. Bedroom)" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="ud-room" />
              <input className="input-teak" placeholder="Budget (e.g. ₹60–90k)" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="ud-budget" />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <input className="input-teak" placeholder="Wood preference" value={form.wood_preference} onChange={(e) => setForm({ ...form, wood_preference: e.target.value })} data-testid="ud-wood" />
              <input className="input-teak" placeholder="Fabric preference" value={form.fabric_preference} onChange={(e) => setForm({ ...form, fabric_preference: e.target.value })} data-testid="ud-fabric" />
            </div>
            <textarea className="input-teak" rows={4} placeholder="Notes / references / dimensions" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ud-notes" />
            <button className="btn-bronze w-full" type="submit" disabled={busy} data-testid="ud-submit">{busy ? "Sending…" : "Send to atelier"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
